/**
 * EthnicityScreen
 *
 * Ethnicity multi-selection — affects genetic screening recommendations.
 * "Prefer not to say" is exclusive (clears other selections).
 */

import React, { useRef, useState } from "react";
import { View, Text, Pressable, ScrollView, Animated } from "react-native";
import { Check } from "lucide-react-native";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import WarmButton from "@louise/moon-ui/primitives/WarmButton";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { usePlanBuilder } from "../PlanBuilderContext";

/** Ethnicity values are stored as stable English IDs (used in patient
 *  data + analytics) and translated to localized labels at render time. */
const ETHNICITIES: { id: string; labelKey: string }[] = [
  { id: "White / Caucasian", labelKey: "plan_builder.ethnicity.white" },
  {
    id: "Black / African American",
    labelKey: "plan_builder.ethnicity.black",
  },
  { id: "Hispanic / Latino", labelKey: "plan_builder.ethnicity.hispanic" },
  { id: "East Asian", labelKey: "plan_builder.ethnicity.east_asian" },
  { id: "South Asian", labelKey: "plan_builder.ethnicity.south_asian" },
  {
    id: "Southeast Asian",
    labelKey: "plan_builder.ethnicity.southeast_asian",
  },
  {
    id: "Middle Eastern / North African",
    labelKey: "plan_builder.ethnicity.middle_eastern",
  },
  {
    id: "Native American / Alaska Native",
    labelKey: "plan_builder.ethnicity.native_american",
  },
  {
    id: "Pacific Islander",
    labelKey: "plan_builder.ethnicity.pacific_islander",
  },
  { id: "Mixed / Multiracial", labelKey: "plan_builder.ethnicity.mixed" },
  {
    id: "Prefer not to say",
    labelKey: "plan_builder.ethnicity.prefer_not_to_say",
  },
];

const EXCLUSIVE = "Prefer not to say";

function OptionButton({
  label,
  isSelected,
  onPress,
}: {
  label: string;
  isSelected: boolean;
  onPress: () => void;
}) {
  const scale = useRef(new Animated.Value(1)).current;

  const handlePressIn = () => {
    Animated.spring(scale, {
      toValue: 0.97,
      useNativeDriver: true,
    }).start();
  };

  const handlePressOut = () => {
    Animated.spring(scale, {
      toValue: 1,
      friction: 4,
      useNativeDriver: true,
    }).start();
  };

  return (
    <Pressable
      onPress={onPress}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      accessibilityRole="button"
      accessibilityState={{ selected: isSelected }}
      // Inert testability hook: react-native-web 0.21 no longer forwards
      // accessibilityState.selected to the DOM, so mirror it via the
      // aria-selected prop for deterministic E2E assertions.
      aria-selected={isSelected}
    >
      <Animated.View
        style={{
          transform: [{ scale }],
          flexDirection: "row",
          alignItems: "center",
          paddingHorizontal: 18,
          paddingVertical: 14,
          backgroundColor: isSelected
            ? `${EMBER.colors.text}10`
            : "rgba(255, 255, 255, 0.82)",
          borderRadius: 14,
          borderWidth: 1,
          borderColor: isSelected
            ? EMBER.colors.text
            : "rgba(255, 255, 255, 0.9)",
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.04,
          shadowRadius: 8,
          justifyContent: "space-between",
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            fontWeight: isSelected ? "500" : "400",
            color: EMBER.colors.text,
          }}
        >
          {label}
        </Text>
        <View
          style={{
            width: 22,
            height: 22,
            borderRadius: 6,
            borderWidth: 2,
            borderColor: isSelected ? EMBER.colors.text : EMBER.colors.border,
            backgroundColor: isSelected ? EMBER.colors.text : "transparent",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isSelected && <Check size={14} color="#FFFFFF" strokeWidth={3} />}
        </View>
      </Animated.View>
    </Pressable>
  );
}

type Props = {
  onNext: () => void;
};

export default function EthnicityScreen({ onNext }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("EthnicityScreen");
  const { state, updateState } = usePlanBuilder();
  const [selected, setSelected] = useState<string[]>(state.ethnicity);

  const handleToggle = (ethnicity: string) => {
    let next: string[];

    if (ethnicity === EXCLUSIVE) {
      // Exclusive — replace everything
      next = selected.includes(ethnicity) ? [] : [ethnicity];
    } else {
      // Regular — toggle, clear exclusive
      const withoutExclusive = selected.filter((e) => e !== EXCLUSIVE);
      if (withoutExclusive.includes(ethnicity)) {
        next = withoutExclusive.filter((e) => e !== ethnicity);
      } else {
        next = [...withoutExclusive, ethnicity];
      }
    }

    setSelected(next);
    updateState({ ethnicity: next });
  };

  return (
    <ScrollView
      contentContainerStyle={{ padding: 24, paddingBottom: 40 }}
      showsVerticalScrollIndicator={false}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 22,
          fontWeight: "700",
          color: EMBER.colors.text,
          lineHeight: 28,
          marginBottom: 8,
        }}
      >
        {t("plan_builder.ethnicity.title")}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 15,
          fontWeight: "400",
          color: EMBER.colors.textLight,
          lineHeight: 22,
          marginBottom: 24,
        }}
      >
        {t("plan_builder.ethnicity.subtitle")}
      </Text>

      <View style={{ gap: 6 }}>
        {ETHNICITIES.map((eth) => (
          <OptionButton
            key={eth.id}
            label={t(eth.labelKey)}
            isSelected={selected.includes(eth.id)}
            onPress={() => {
              capturePress(pressLabel(t(eth.labelKey)), eth.id);
              handleToggle(eth.id);
            }}
          />
        ))}
      </View>

      {selected.length > 0 && (
        <View style={{ marginTop: 24 }}>
          <WarmButton title={t("plan_builder.continue")} onPress={onNext} />
        </View>
      )}
    </ScrollView>
  );
}
